import { useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  X, FileUp, Loader2, Check, ChevronDown, ChevronUp, AlertTriangle, Wand2,
} from "lucide-react";
import { readMarkdownFile, extractPdfText, fileBaseName } from "@/core/import";
import { proposeNotesFromText, splitMarkdown, type ProposedNote } from "@/core/aiImport";
import { useLibraryContentStore, newSubId } from "@/store/libraryContentStore";
import { groupOfDiscipline, mainIdForGroup } from "@/content/library";
import { useCategories } from "@/store/customContentStore";
import { useAIStore } from "@/store/aiStore";
import { getProvider } from "@/core/ai";
import { AIError } from "@/core/ai/types";

type Phase = "pick" | "ready" | "working" | "review";

interface Row extends ProposedNote {
  rowId: string;
  include: boolean;
  mainId: string;
  mainTouched: boolean;
  open: boolean;
}

export default function ImportWizard({ onClose, onDone }: { onClose: () => void; onDone?: (count: number) => void }) {
  const mainSessions = useLibraryContentStore((s) => s.mainSessions);
  const saveSub = useLibraryContentStore((s) => s.saveSub);
  const CATEGORIES = useCategories();
  const aiOn = useAIStore((s) => s.isConfigured());
  const apiKey = useAIStore((s) => s.apiKey);
  const model = useAIStore((s) => s.model);

  const mains = useMemo(() => [...mainSessions].sort((a, b) => a.order - b.order), [mainSessions]);

  const [phase, setPhase] = useState<Phase>("pick");
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [rows, setRows] = useState<Row[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const sourceName = file ? fileBaseName(file.name) : "";

  function deriveMain(disciplineId: string): string {
    const target = mains.find((m) => m.id === mainIdForGroup(groupOfDiscipline(disciplineId || undefined)));
    return target?.id ?? mains[0]?.id ?? "";
  }

  function pick(f: File | undefined | null) {
    if (!f) return;
    setError("");
    setFile(f);
    setPhase("ready");
  }

  async function run() {
    if (!file) return;
    setError("");
    setPhase("working");
    setProgress({ done: 0, total: 1 });
    try {
      const isPdf = /\.pdf$/i.test(file.name) || file.type === "application/pdf";
      const text = isPdf ? await extractPdfText(file) : await readMarkdownFile(file);
      if (!text.trim()) {
        setError(isPdf ? "No selectable text found — this PDF may be scanned images." : "That file was empty.");
        setPhase("ready");
        return;
      }
      let notes: ProposedNote[];
      if (aiOn) {
        abortRef.current = new AbortController();
        const provider = getProvider({ apiKey, model });
        notes = await proposeNotesFromText(provider, text, fileBaseName(file.name), (d, t) => setProgress({ done: d, total: t }), abortRef.current.signal);
      } else {
        notes = isPdf
          ? [{ title: fileBaseName(file.name), disciplineId: "", summary: "", tags: [], body: text }]
          : splitMarkdown(text);
      }
      if (!notes.length) {
        setError("No notes could be extracted from that file.");
        setPhase("ready");
        return;
      }
      setRows(
        notes.map((n, i) => ({ ...n, rowId: `r${i}`, include: true, mainId: deriveMain(n.disciplineId), mainTouched: false, open: false }))
      );
      setPhase("review");
    } catch (e) {
      if ((e as { name?: string })?.name === "AbortError") {
        setPhase("ready");
        return;
      }
      setError(e instanceof AIError ? e.message : "Import failed. Check the file, and your AI key in Settings.");
      setPhase("ready");
    }
  }

  function cancelRun() {
    abortRef.current?.abort();
    setPhase("ready");
  }

  function patch(rowId: string, p: Partial<Row>) {
    setRows((rs) => rs.map((r) => (r.rowId === rowId ? { ...r, ...p } : r)));
  }

  function setDiscipline(rowId: string, disciplineId: string) {
    setRows((rs) =>
      rs.map((r) => (r.rowId === rowId ? { ...r, disciplineId, mainId: r.mainTouched ? r.mainId : deriveMain(disciplineId) } : r))
    );
  }

  function commit() {
    const chosen = rows.filter((r) => r.include && r.title.trim() && r.body.trim() && r.mainId);
    for (const r of chosen) {
      saveSub({
        id: newSubId(),
        mainId: r.mainId,
        title: r.title.trim(),
        body: r.body.trim(),
        disciplineId: r.disciplineId || undefined,
        sources: sourceName ? [{ title: sourceName }] : [],
        tags: r.tags,
        order: 0,
      });
    }
    onDone?.(chosen.length);
    onClose();
  }

  const selectedCount = rows.filter((r) => r.include).length;
  const mainName = (id: string) => mains.find((m) => m.id === id)?.title ?? "—";

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, y: 12, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        className="relative flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-2xl"
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div>
            <h3 className="font-display text-lg font-semibold text-text">Import & auto-organize</h3>
            <p className="text-xs text-muted">Drop a PDF or Markdown file — it's split into notes, distilled, and filed into your sessions.</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="grid h-8 w-8 place-items-center rounded-lg border border-border text-muted hover:bg-surface-2 hover:text-text">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {error && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-400/40 bg-amber-400/10 px-3 py-2 text-sm text-amber-300">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {error}
            </div>
          )}

          {/* Pick / ready */}
          {(phase === "pick" || phase === "ready") && (
            <>
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); pick(e.dataTransfer.files?.[0]); }}
                onClick={() => fileInput.current?.click()}
                className={[
                  "grid cursor-pointer place-items-center rounded-2xl border-2 border-dashed px-6 py-10 text-center transition-colors",
                  dragOver ? "border-primary bg-primary/5" : "border-border hover:border-primary/50",
                ].join(" ")}
              >
                <FileUp className="mb-2 h-8 w-8 text-muted" />
                <p className="text-sm text-text">{file ? <span className="font-medium">{file.name}</span> : "Click to choose, or drop a file here"}</p>
                <p className="mt-1 text-xs text-muted">PDF, Markdown, or plain text</p>
                <input ref={fileInput} type="file" accept=".pdf,application/pdf,.md,.markdown,.txt,text/markdown,text/plain" hidden onChange={(e) => pick(e.target.files?.[0])} />
              </div>

              <div className={["rounded-lg border px-3 py-2 text-xs", aiOn ? "border-primary/30 bg-primary/5 text-text/80" : "border-border bg-surface-2/50 text-muted"].join(" ")}>
                {aiOn ? (
                  <>AI is on — sections will be distilled into original notes, classified by discipline, and auto-filed into the matching session. Uses your Anthropic key (pay-per-use).</>
                ) : (
                  <>AI is off — a Markdown file is split by its headings; a PDF becomes one note. Turn on AI in <span className="text-text">Settings</span> for automatic distilling & classification.</>
                )}
              </div>

              {file && (
                <button onClick={run} className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-fg shadow-glow transition-transform hover:scale-[1.01]">
                  {aiOn ? <Wand2 className="h-4 w-4" /> : <FileUp className="h-4 w-4" />} {aiOn ? "Analyze & organize" : "Split into sections"}
                </button>
              )}
            </>
          )}

          {/* Working */}
          {phase === "working" && (
            <div className="grid place-items-center gap-3 py-12 text-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-text">{aiOn ? "Distilling and classifying…" : "Extracting…"}</p>
              {progress.total > 1 && <p className="text-xs text-muted">Chunk {Math.min(progress.done + 1, progress.total)} of {progress.total}</p>}
              <button onClick={cancelRun} className="mt-1 rounded-lg border border-border px-3 py-1.5 text-xs text-muted hover:text-text">Cancel</button>
            </div>
          )}

          {/* Review */}
          {phase === "review" && (
            <div className="space-y-2">
              <p className="text-sm text-muted">{rows.length} notes found · {selectedCount} selected. Adjust titles, discipline, and target session, then add them.</p>
              {rows.map((r) => (
                <div key={r.rowId} className={["rounded-xl border bg-surface-2/40 p-3", r.include ? "border-border" : "border-border/50 opacity-60"].join(" ")}>
                  <div className="flex items-start gap-2">
                    <input type="checkbox" checked={r.include} onChange={(e) => patch(r.rowId, { include: e.target.checked })} className="mt-1.5 h-4 w-4 shrink-0 accent-primary" />
                    <div className="min-w-0 flex-1 space-y-2">
                      <input
                        value={r.title}
                        onChange={(e) => patch(r.rowId, { title: e.target.value })}
                        className="w-full rounded-lg border border-border bg-bg px-2.5 py-1.5 text-sm font-medium text-text focus:border-primary focus:outline-none"
                        placeholder="Note title"
                      />
                      <div className="grid gap-2 sm:grid-cols-2">
                        <select value={r.disciplineId} onChange={(e) => setDiscipline(r.rowId, e.target.value)} className="w-full rounded-lg border border-border bg-bg px-2.5 py-1.5 text-xs text-text focus:border-primary focus:outline-none">
                          <option value="">Unclassified</option>
                          {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.group} · {c.name}</option>)}
                        </select>
                        <select value={r.mainId} onChange={(e) => patch(r.rowId, { mainId: e.target.value, mainTouched: true })} className="w-full rounded-lg border border-border bg-bg px-2.5 py-1.5 text-xs text-text focus:border-primary focus:outline-none">
                          {mains.map((m) => <option key={m.id} value={m.id}>→ {m.title}</option>)}
                        </select>
                      </div>
                      {r.summary && <p className="text-xs text-muted">{r.summary}</p>}
                      <button onClick={() => patch(r.rowId, { open: !r.open })} className="inline-flex items-center gap-1 text-[11px] text-muted hover:text-text">
                        {r.open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />} {r.open ? "Hide" : "Preview"} note
                      </button>
                      {r.open && (
                        <pre className="max-h-56 overflow-auto whitespace-pre-wrap rounded-lg border border-border bg-bg p-2.5 text-xs text-text/90">{r.body}</pre>
                      )}
                    </div>
                    <span className="shrink-0 rounded-full bg-surface px-2 py-0.5 text-[10px] text-muted">{mainName(r.mainId)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {phase === "review" && (
          <div className="flex items-center justify-between gap-2 border-t border-border px-5 py-3">
            <span className="text-xs text-muted">Notes are added as editable sub-sessions you can move or delete anytime.</span>
            <div className="flex gap-2">
              <button onClick={onClose} className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted hover:text-text">Cancel</button>
              <button onClick={commit} disabled={selectedCount === 0} className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-fg shadow-glow transition-transform enabled:hover:scale-[1.02] disabled:opacity-40">
                <Check className="h-4 w-4" /> Add {selectedCount} to Library
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
